import { createContext } from '@hover/blueprint';
import type { PropsWithChildren, RefObject } from 'react';
import React, { useCallback, useImperativeHandle, useRef } from 'react';

import { useCustomizeTargetTracking } from 'src/modules/homeownerApp/components/Customize/useCustomizeTargetTracking';
import { useHomeownerAppContext } from 'src/modules/homeownerApp/hooks';
import { useMemoContextValue } from 'src/shared/hooks';
import type {
  ButtonImpression,
  ButtonPressed,
  ModuleViewed,
  ProductSelected,
} from 'src/shared/telemetry';
import {
  buttonImpression,
  buttonPressed,
  moduleViewed,
  productSelected,
} from 'src/shared/telemetry';

export interface TrackingRef {
  buttonImpression: (
    trackingData: Omit<ButtonImpression, 'page_or_screen_name'>,
  ) => void;
  buttonPressed: (
    trackingData: Omit<ButtonPressed, 'page_or_screen_name'>,
  ) => void;
  moduleViewed: (
    trackingData: Omit<ModuleViewed, 'page_or_screen_name'>,
  ) => void;
  productSelected: (
    trackingData: Omit<ProductSelected, 'page_or_screen_name'>,
  ) => void;
}

const defaultTrackingRef = {
  buttonImpression: () => {},
  buttonPressed: () => {},
  moduleViewed: () => {},
  productSelected: () => {},
};

const useImperativeTrackingRef = () => useRef<TrackingRef>(defaultTrackingRef);

export interface CustomizeTrackingContext {
  trackingRef: RefObject<TrackingRef>;
}

const [Provider, useContext] = createContext<CustomizeTrackingContext>({
  name: 'CustomizeTrackingContext',
});

const logTracking = (
  trackingEvent: string,
  trackingPayload: Record<string, unknown>,
) => {
  if (__DEV__) {
    // eslint-disable-next-line no-console
    console.log(trackingEvent, trackingPayload);
  }
};

interface ImperativeTrackingProps {
  trackingRef: RefObject<TrackingRef>;
  includeCustomizeTargetTracking?: boolean;
}

const ImperativeTracking = ({
  trackingRef,
  includeCustomizeTargetTracking = false,
}: ImperativeTrackingProps) => {
  const { trackingData: homeownerAppTracking } = useHomeownerAppContext();
  const homeownerAppTrackingRef = useRef(homeownerAppTracking);
  homeownerAppTrackingRef.current = homeownerAppTracking;

  const customizeTargetTracking = useCustomizeTargetTracking();
  const customizeTargetTrackingRef = useRef(customizeTargetTracking);
  customizeTargetTrackingRef.current = customizeTargetTracking;

  const buildBaseTracking = useCallback(
    () => ({
      ...homeownerAppTrackingRef.current,
      ...(includeCustomizeTargetTracking
        ? customizeTargetTrackingRef.current
        : undefined),
    }),
    [
      customizeTargetTrackingRef,
      homeownerAppTrackingRef,
      includeCustomizeTargetTracking,
    ],
  );

  useImperativeHandle(
    trackingRef,
    () => ({
      buttonImpression: (
        trackingData: Omit<ButtonImpression, 'page_or_screen_name'>,
      ) => {
        const merged = {
          ...buildBaseTracking(),
          ...trackingData,
        };
        logTracking('buttonImpression', merged);
        buttonImpression(merged);
      },
      buttonPressed: (
        trackingData: Omit<ButtonPressed, 'page_or_screen_name'>,
      ) => {
        const merged = {
          ...buildBaseTracking(),
          ...trackingData,
        };
        logTracking('buttonPressed', merged);
        buttonPressed(merged);
      },
      moduleViewed: (
        trackingData: Omit<ModuleViewed, 'page_or_screen_name'>,
      ) => {
        const merged = {
          ...buildBaseTracking(),
          ...trackingData,
        };
        logTracking('moduleViewed', merged);
        moduleViewed(merged);
      },
      productSelected: (
        trackingData: Omit<ProductSelected, 'page_or_screen_name'>,
      ) => {
        const merged = {
          ...buildBaseTracking(),
          ...trackingData,
        };
        logTracking('productSelected', merged);
        productSelected(merged);
      },
    }),
    [buildBaseTracking],
  );

  return null;
};

/**
 * ImperativeTrackingProvider enables the use of context tracking values
 * without the downside of causing re-renders every time the context changes. It
 * accomplishes this by rendering the context hooks in leaf component and
 * then using an imperative handle to pass tracking methods back to the
 * provider. Since the hooks are isolated in a leaf component, any re-renders
 * triggered by changes in the context will be isolated to the leaf component.
 * The tracking methods attached to the imperative handle always have access to
 * the latest context tracking values. The provider then exposes this ref via
 * context. Because the ref exposed via context never changes, the
 * ImperativeTrackingProvider will not cause components consuming its context to
 * rerender
 */
export const ImperativeTrackingProvider = ({
  children,
  ...props
}: PropsWithChildren<Omit<ImperativeTrackingProps, 'trackingRef'>>) => {
  const trackingRef = useImperativeTrackingRef();

  return (
    <>
      <Provider value={useMemoContextValue({ trackingRef })}>
        {children}
      </Provider>
      <ImperativeTracking
        {...props}
        includeCustomizeTargetTracking
        trackingRef={trackingRef}
      />
    </>
  );
};

export const useImperativeTracking = () => useContext();
